import { lazy, Suspense } from "react";
import { Routes, Route, Navigate, useParams } from "react-router-dom";
import { DEMO_STORE_SLUG } from "@/data/demoStorefrontData";
import { getStoreTemplate } from "@/data/storeTemplates";
import { templatePreviewEntrySuffix } from "@/lib/templatePreviewEntry";
import { AuthGuard } from "@/components/guards/AuthGuard";
import { StoreRequiredGuard } from "@/components/guards/StoreRequiredGuard";
import { AdminGuard } from "@/components/guards/AdminGuard";
import { StoreProvider } from "@/contexts/StoreContext";
import { DashboardV2LegacyRedirect } from "@/routes/DashboardV2LegacyRedirect";

// Inline spinner — keeps lucide-react (vendor-icons) off the routes module's
// static import graph so it doesn't force that chunk into the initial bundle.
function Spinner({ className = "h-8 w-8" }: { className?: string }) {
  return (
    <svg
      className={`animate-spin text-muted-foreground ${className}`}
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
    </svg>
  );
}

const Landing = lazy(() => import("@/pages/Landing"));
const Login = lazy(() => import("@/pages/Login"));
const Signup = lazy(() => import("@/pages/Signup"));
const AuthCallbackPage = lazy(() => import("@/pages/AuthCallbackPage"));
const VerifyEmail = lazy(() => import("@/pages/VerifyEmail"));
const AcceptInvite = lazy(() => import("@/pages/AcceptInvite"));
const V2DashboardTemplates = lazy(() => import("@/pages/dashboard-v2/Templates"));

/** Catalogue templates marketing — sans login (StoreProvider pour hooks compatibles). */
function PublicTemplatesEntry() {
  return (
    <StoreProvider>
      <Suspense
        fallback={
          <div className="flex min-h-screen items-center justify-center bg-background">
            <Spinner />
          </div>
        }
      >
        <V2DashboardTemplates audience="public" />
      </Suspense>
    </StoreProvider>
  );
}

function DashboardLayoutFallback() {
  return (
    <div className="min-h-screen bg-muted lg:pl-[290px]">
      <aside className="fixed bottom-4 left-4 top-4 hidden w-[260px] animate-pulse rounded-[24px] bg-card/80 shadow-sm ring-1 ring-border/60 lg:block" />
      <main className="space-y-5 p-4 lg:p-8">
        <div className="h-14 animate-pulse rounded-2xl bg-card shadow-sm ring-1 ring-border/60" />
        <div className="h-8 w-60 animate-pulse rounded-lg bg-muted" />
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-28 animate-pulse rounded-2xl bg-card shadow-sm ring-1 ring-border/60" />
          ))}
        </div>
        <div className="h-72 animate-pulse rounded-2xl bg-card shadow-sm ring-1 ring-border/60" />
      </main>
    </div>
  );
}

const EmailCampaigns = lazy(() => import("@/pages/EmailCampaigns"));
const EmailAnalytics = lazy(() => import("@/pages/EmailAnalytics"));
const DashboardLayoutV2 = lazy(() =>
  import("@/components/dashboard-v2/DashboardLayoutV2").then((m) => ({
    default: m.DashboardLayoutV2,
  }))
);
const V2DashboardHome = lazy(() => import("@/pages/dashboard-v2/DashboardHome"));
const V2Products = lazy(() => import("@/pages/dashboard-v2/Products"));
const V2CreateProduct = lazy(() => import("@/pages/dashboard-v2/CreateProduct"));
const V2EditProduct = lazy(() => import("@/pages/dashboard-v2/EditProduct"));
const V2Categories = lazy(() => import("@/pages/dashboard-v2/Categories"));
const V2DashboardPages = lazy(() => import("@/pages/dashboard-v2/DashboardPages"));
const V2Orders = lazy(() => import("@/pages/dashboard-v2/Orders"));
const V2OrderDetail = lazy(() => import("@/pages/dashboard-v2/OrderDetail"));
const V2Customers = lazy(() => import("@/pages/dashboard-v2/Customers"));
const V2CustomerDetail = lazy(() => import("@/pages/dashboard-v2/CustomerDetail"));
const V2Analytics = lazy(() => import("@/pages/dashboard-v2/Analytics"));
const V2Warehouses = lazy(() => import("@/pages/dashboard-v2/Warehouses"));
const V2CRM = lazy(() => import("@/pages/dashboard-v2/CRM"));
const V2Settings = lazy(() => import("@/pages/dashboard-v2/Settings"));
const V2Team = lazy(() => import("@/pages/dashboard-v2/Team"));
const V2StoreEditor = lazy(() => import("@/pages/dashboard-v2/editor/StoreEditor"));
const V2Dropshipping = lazy(() => import("@/pages/dashboard-v2/Dropshipping"));
const V2Marketing = lazy(() => import("@/pages/dashboard-v2/Marketing"));
const V2Billing = lazy(() => import("@/pages/dashboard-v2/Billing"));
const V2PaiementEnLigne = lazy(() => import("@/pages/dashboard-v2/PaiementEnLigne"));
const V2Reviews = lazy(() => import("@/pages/dashboard-v2/Reviews"));
const V2Automation = lazy(() => import("@/pages/dashboard-v2/Automation"));
const V2Calculator = lazy(() => import("@/pages/dashboard-v2/Calculator"));
const V2Apps = lazy(() => import("@/pages/dashboard-v2/Apps"));
const V2EmailTemplates = lazy(() => import("@/pages/dashboard-v2/EmailTemplates"));
const V2EmailTemplateEditor = lazy(() => import("@/pages/dashboard-v2/EmailTemplateEditor"));
const V2EmailCampaigns = lazy(() => import("@/pages/dashboard-v2/EmailCampaigns"));
const V2ShippingSettings = lazy(() => import("@/pages/dashboard-v2/ShippingSettings"));
const V2TaxSettings = lazy(() => import("@/pages/dashboard-v2/TaxSettings"));
const V2Shipments = lazy(() => import("@/pages/dashboard-v2/Shipments"));
const V2Support = lazy(() => import("@/pages/dashboard-v2/Support"));
const V2SupportDetail = lazy(() => import("@/pages/dashboard-v2/SupportDetail"));
const V2Returns = lazy(() => import("@/pages/dashboard-v2/Returns"));
const V2ReturnDetail = lazy(() => import("@/pages/dashboard-v2/ReturnDetail"));
const V2AutomationRules = lazy(() => import("@/pages/dashboard-v2/AutomationRules"));
const PublicSalesPage = lazy(() => import("@/pages/PublicSalesPage"));
const NotFound = lazy(() => import("@/pages/NotFound"));
const Maintenance = lazy(() => import("@/pages/Maintenance"));
const PreviewLayout = lazy(() =>
  import("@/components/store/preview/PreviewLayout").then((m) => ({ default: m.PreviewLayout }))
);
const PreviewNotFound = lazy(() =>
  import("@/components/store/preview/PreviewNotFound").then((m) => ({ default: m.PreviewNotFound }))
);
const AdminLayout = lazy(() => import("@/layouts/AdminLayout"));
const AdminOverview = lazy(() => import("@/pages/admin/Overview"));
const AdminStores = lazy(() => import("@/pages/admin/Stores"));
const AdminStoreDetail = lazy(() => import("@/pages/admin/StoreDetail"));
const AdminUsers = lazy(() => import("@/pages/admin/Users"));
const AdminSubscriptions = lazy(() => import("@/pages/admin/Subscriptions"));
const AdminInvoices = lazy(() => import("@/pages/admin/Invoices"));
const AdminSettings = lazy(() => import("@/pages/admin/Settings"));
const AdminRevenue = lazy(() => import("@/pages/admin/Revenue"));
const AdminVerification = lazy(() => import("@/pages/admin/VerificationQueue"));
const AdminAnnouncements = lazy(() => import("@/pages/admin/Announcements"));
const AdminPromoCodes = lazy(() => import("@/pages/admin/PromoCodes"));
const AdminAnalyticsPlus = lazy(() => import("@/pages/admin/AnalyticsPlus"));
const AdminAuditLogs = lazy(() => import("@/pages/admin/AuditLogs"));
const AdminManualPayments = lazy(() => import("@/pages/admin/ManualPayments"));
const AdminSupportInbox = lazy(() => import("@/pages/admin/SupportInbox"));
const AdminFeatureAccess = lazy(() => import("@/pages/admin/FeatureAccess"));
const AdminUsageMonitor = lazy(() => import("@/pages/admin/UsageMonitor"));
const FeaturesPage = lazy(() => import("@/pages/FeaturesPage"));
const IntegrationsPage = lazy(() => import("@/pages/IntegrationsPage"));
const Pricing = lazy(() => import("@/pages/Pricing"));
const PlanManagement = lazy(() => import("@/pages/PlanManagement"));
const Help = lazy(() => import("@/pages/Help"));
const Terms = lazy(() => import("@/pages/Terms"));
const Privacy = lazy(() => import("@/pages/Privacy"));
const Contact = lazy(() => import("@/pages/Contact"));
const Onboarding = lazy(() => import("@/pages/Onboarding"));
const OrderSuccess = lazy(() => import("@/pages/OrderSuccess"));
const OrderFailed = lazy(() => import("@/pages/OrderFailed"));
const ForgotPassword = lazy(() => import("@/pages/ForgotPassword"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const Blog = lazy(() => import("@/pages/Blog"));
const BlogPost = lazy(() => import("@/pages/BlogPost"));
const Academy = lazy(() => import("@/pages/Academy"));
const Documentation = lazy(() => import("@/pages/Documentation"));
const FAQ = lazy(() => import("@/pages/FAQ"));
const LegalMentionsLegalesPage = lazy(() => import("@/pages/legal/MentionsLegalesPage"));
const LegalTermsPage = lazy(() => import("@/pages/legal/LegalTermsPage"));
const LegalAupPage = lazy(() => import("@/pages/legal/LegalAupPage"));
const LegalPrivacyPage = lazy(() => import("@/pages/legal/LegalPrivacyPage"));
const LegalRefundPage = lazy(() => import("@/pages/legal/LegalRefundPage"));
const LegalCookiesPage = lazy(() => import("@/pages/legal/LegalCookiesPage"));
const TrackOrder = lazy(() => import("@/pages/TrackOrder"));
const TemplatePreviewPage = lazy(() => import("@/pages/TemplatePreviewPage"));
const TemplatePreviewStorefront = lazy(() =>
  import("@/pages/TemplatePreviewPage").then((m) => ({ default: m.TemplatePreviewStorefront }))
);
const ReferenceStorefrontLayout = lazy(() => import("@/storefront/layout/ReferenceStorefrontLayout"));
const RuntimePage = lazy(() => import("@/storefront-runtime/runtime/RuntimePage"));
const StorefrontEditorPro = lazy(() => import("@/editor/EditorPage"));
const BuyerLoginPage = lazy(() => import("@/storefront/pages/BuyerLoginPage"));
const BuyerRegisterPage = lazy(() => import("@/storefront/pages/BuyerRegisterPage"));
const BuyerAccountPage = lazy(() => import("@/storefront/pages/BuyerAccountPage"));
const BuyerOrdersPage = lazy(() => import("@/storefront/pages/BuyerOrdersPage"));
const BuyerVerifyPage = lazy(() => import("@/storefront/pages/BuyerVerifyPage"));
const ReferenceJournalPostPage = lazy(() => import("@/storefront/pages/ReferenceJournalPostPage"));

// ── Legacy redirect helpers ────────────────────────────────────────────────────
// Redirect old /store/:storeSlug paths to the new /ref/:storeSlug block system.
// Using <Navigate> keeps the URL clean and avoids double-rendering.

function StoreSlugRedirect({ subPath }: { subPath?: string }) {
  const params = useParams<{ storeSlug: string; productSlug?: string; categorySlug?: string; collectionSlug?: string; page?: string }>()
  const { storeSlug, productSlug, categorySlug, collectionSlug, page } = params
  if (!storeSlug) return <Navigate to="/" replace />

  let to = `/ref/${storeSlug}`
  if (subPath === 'product' && productSlug) to += `/product/${productSlug}`
  else if (subPath === 'category' && (categorySlug || collectionSlug)) to += `/category/${categorySlug ?? collectionSlug}`
  else if (subPath === 'checkout') to += '/checkout'
  else if (page) to += `/${page}`

  return <Navigate to={to} replace />
}

function StoreIdRedirect() {
  // /s/:storeId short URLs — redirect to landing (storeId ≠ slug, can't resolve without DB)
  return <Navigate to="/" replace />
}

/** Lands on each template's own home (if it has blocks), else shop — no shared default by template id. */
function TemplatePreviewEntryRedirect() {
  const { templateId } = useParams<{ templateId: string }>()
  const t = templateId ? getStoreTemplate(templateId) : null
  const suffix = templatePreviewEntrySuffix(t?.layout ?? null)
  return <Navigate to={`ref/${DEMO_STORE_SLUG}${suffix}`} replace relative="path" />
}

/** Full marketing + dashboard + /store/:slug routes (apex domain). */
export function MainAppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/features" element={<FeaturesPage />} />
      <Route path="/integrations" element={<IntegrationsPage />} />
      <Route
        path="/pricing"
        element={
          <StoreProvider>
            <Suspense
              fallback={
                <div className="flex min-h-screen items-center justify-center bg-background">
                  <Spinner />
                </div>
              }
            >
              <Pricing />
            </Suspense>
          </StoreProvider>
        }
      />
      <Route
        path="/plan-management"
        element={
          <StoreProvider>
            <Suspense
              fallback={
                <div className="flex min-h-screen items-center justify-center bg-background">
                  <Spinner />
                </div>
              }
            >
              <PlanManagement />
            </Suspense>
          </StoreProvider>
        }
      />
      <Route path="/contact" element={<Contact />} />
      <Route path="/help" element={<Help />} />
      <Route path="/legal/mentions-legales" element={<LegalMentionsLegalesPage />} />
      <Route path="/legal/terms" element={<LegalTermsPage />} />
      <Route path="/legal/aup" element={<LegalAupPage />} />
      <Route path="/legal/privacy" element={<LegalPrivacyPage />} />
      <Route path="/legal/refund" element={<LegalRefundPage />} />
      <Route path="/legal/cookies" element={<LegalCookiesPage />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/blog" element={<Blog />} />
      <Route path="/blog/:postId" element={<BlogPost />} />
      <Route path="/academy" element={<Academy />} />
      <Route path="/docs" element={<Documentation />} />
      <Route path="/faq" element={<FAQ />} />
      <Route
        path="/ref/:storeSlug"
        element={
          <Suspense
            fallback={
              <div className="flex min-h-screen items-center justify-center bg-muted">
                <Spinner />
              </div>
            }
          >
            <ReferenceStorefrontLayout />
          </Suspense>
        }
      >
        <Route index element={<RuntimePage />} />
        <Route path="shop" element={<RuntimePage />} />
        <Route path="category" element={<Navigate to="../shop" replace />} />
        <Route path="category/null" element={<Navigate to="../shop" replace />} />
        <Route path="category/undefined" element={<Navigate to="../shop" replace />} />
        <Route path="product/:productSlug" element={<RuntimePage />} />
        <Route path="category/:categorySlug" element={<RuntimePage />} />
        <Route path="cart" element={<RuntimePage />} />
        <Route path="checkout" element={<RuntimePage />} />
        <Route path="search" element={<RuntimePage />} />
        <Route path="about" element={<RuntimePage />} />
        <Route path="contact" element={<RuntimePage />} />
        <Route path="faq" element={<RuntimePage />} />
        <Route path="journal" element={<RuntimePage />} />
        <Route path="journal/:postSlug" element={<ReferenceJournalPostPage />} />
        <Route path="policy/shipping" element={<RuntimePage />} />
        <Route path="policy/returns" element={<RuntimePage />} />
        <Route path="account" element={<BuyerAccountPage />} />
        <Route path="account/login" element={<BuyerLoginPage />} />
        <Route path="account/register" element={<BuyerRegisterPage />} />
        <Route path="account/orders" element={<BuyerOrdersPage />} />
        <Route path="account/verify" element={<BuyerVerifyPage />} />
      </Route>
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/accept-invite" element={<AcceptInvite />} />
      <Route path="/auth/callback" element={<AuthCallbackPage />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      {/* Catalogue templates — public (sans compte) */}
      <Route path="/templates" element={<PublicTemplatesEntry />} />
      <Route path="/templates-showcase" element={<Navigate to="/templates" replace />} />
      <Route path="/templates-showcase/*" element={<Navigate to="/templates" replace />} />
      <Route
        path="/onboarding"
        element={
          <AuthGuard>
            <StoreProvider>
              <Onboarding />
            </StoreProvider>
          </AuthGuard>
        }
      />
      <Route path="/mock-editor" element={<NotFound />} />
      {/*
        Template preview — démo uniquement (données fictives). Sans login : pas d'« Appliquer ».
        Avec boutique active : appliquer écrit sur le store courant.
      */}
      <Route
        path="/preview-template/:templateId"
        element={
          <StoreProvider>
            <Suspense
              fallback={
                <div className="flex min-h-screen items-center justify-center bg-muted">
                  <Spinner />
                </div>
              }
            >
              <TemplatePreviewPage />
            </Suspense>
          </StoreProvider>
        }
      >
        <Route
          index
          element={<TemplatePreviewEntryRedirect />}
        />
        <Route
          path="ref/:storeSlug"
          element={
            <Suspense
              fallback={
                <div className="flex min-h-screen items-center justify-center bg-muted">
                  <Spinner />
                </div>
              }
            >
              <TemplatePreviewStorefront />
            </Suspense>
          }
        >
          <Route index element={<RuntimePage />} />
          <Route path="shop" element={<RuntimePage />} />
          <Route path="category" element={<Navigate to="../shop" replace />} />
          <Route path="category/null" element={<Navigate to="../shop" replace />} />
          <Route path="category/undefined" element={<Navigate to="../shop" replace />} />
          <Route path="product/:productSlug" element={<RuntimePage />} />
          <Route path="category/:categorySlug" element={<RuntimePage />} />
          <Route path="cart" element={<RuntimePage />} />
          <Route path="checkout" element={<RuntimePage />} />
          <Route path="search" element={<RuntimePage />} />
          <Route path="about" element={<RuntimePage />} />
          <Route path="contact" element={<RuntimePage />} />
          <Route path="faq" element={<RuntimePage />} />
          <Route path="journal" element={<RuntimePage />} />
          <Route path="journal/:postSlug" element={<ReferenceJournalPostPage />} />
          <Route path="policy/shipping" element={<RuntimePage />} />
          <Route path="policy/returns" element={<RuntimePage />} />
          <Route path="account" element={<BuyerAccountPage />} />
          <Route path="account/login" element={<BuyerLoginPage />} />
          <Route path="account/register" element={<BuyerRegisterPage />} />
          <Route path="account/orders" element={<BuyerOrdersPage />} />
          <Route path="account/verify" element={<BuyerVerifyPage />} />
        </Route>
      </Route>
      {/* Legacy builder-v2 URLs → redirect to the block editor */}
      <Route path="/builder-v2" element={<Navigate to="/dashboard/storefront-editor-pro" replace />} />
      <Route path="/builder-v2/*" element={<Navigate to="/dashboard/storefront-editor-pro" replace />} />
      <Route
        path="/dashboard"
        element={
          <AuthGuard>
            <StoreProvider>
              <StoreRequiredGuard>
                <Suspense fallback={<DashboardLayoutFallback />}>
                  <DashboardLayoutV2 />
                </Suspense>
              </StoreRequiredGuard>
            </StoreProvider>
          </AuthGuard>
        }
      >
        <Route index element={<V2DashboardHome />} />
        <Route path="products" element={<V2Products />} />
        <Route path="products/create" element={<V2CreateProduct />} />
        <Route path="products/:id/edit" element={<V2EditProduct />} />
        <Route path="categories" element={<V2Categories />} />
        <Route path="pages" element={<V2DashboardPages />} />
        <Route
          path="pages/create"
          element={<Navigate to="/dashboard/storefront-editor-pro" replace />}
        />
        <Route path="orders" element={<V2Orders />} />
        <Route path="orders/:id" element={<V2OrderDetail />} />
        <Route path="returns" element={<V2Returns />} />
        <Route path="returns/:id" element={<V2ReturnDetail />} />
        <Route path="customers" element={<V2Customers />} />
        <Route path="customers/:id" element={<V2CustomerDetail />} />
        <Route path="analytics" element={<V2Analytics />} />
        <Route path="calculator" element={<V2Calculator />} />
        <Route path="warehouses" element={<V2Warehouses />} />
        <Route path="crm" element={<V2CRM />} />
        <Route path="automation" element={<V2Automation />} />
        <Route path="automation-rules" element={<V2AutomationRules />} />
        <Route
          path="payments"
          element={<Navigate to="/dashboard/paiement-en-ligne" replace />}
        />
        <Route path="billing" element={<V2Billing />} />
        <Route path="paiement-en-ligne" element={<V2PaiementEnLigne />} />
        <Route path="settings" element={<V2Settings />} />
        <Route path="settings/taxes" element={<V2TaxSettings />} />
        <Route path="operations/taxes" element={<V2TaxSettings />} />
        <Route path="team" element={<V2Team />} />
        <Route path="store-editor" element={<V2StoreEditor />} />
        <Route path="editor/:templateId" element={<V2StoreEditor />} />
        <Route path="store/:storeId/editor-v2" element={<Navigate to="/dashboard/storefront-editor-pro" replace />} />
        <Route path="storefront-editor-pro" element={<StorefrontEditorPro />} />
        <Route path="dropshipping" element={<V2Dropshipping />} />
        <Route path="marketing" element={<V2Marketing />} />
        <Route path="reviews" element={<V2Reviews />} />
        <Route path="apps" element={<V2Apps />} />
        <Route path="templates" element={<V2DashboardTemplates />} />
        <Route path="email-templates" element={<V2EmailTemplates />} />
        <Route path="editor/email/:id" element={<V2EmailTemplateEditor />} />
        <Route path="email-campaigns" element={<V2EmailCampaigns />} />
        <Route path="email-automations" element={<V2EmailCampaigns />} />
        <Route path="email-analytics" element={<EmailAnalytics />} />
        <Route path="shipping-settings" element={<V2ShippingSettings />} />
        <Route path="shipments" element={<V2Shipments />} />
        <Route path="support" element={<V2Support />} />
        <Route path="support/:ticketId" element={<V2SupportDetail />} />
      </Route>
      {/* Ancien préfixe /dashboard-v2 (liens sauvegardés) → /dashboard */}
      <Route path="/dashboard" element={<DashboardV2LegacyRedirect />} />
      <Route path="/dashboard/*" element={<DashboardV2LegacyRedirect />} />
      <Route
        path="/admin"
        element={
          <AdminGuard>
            <AdminLayout />
          </AdminGuard>
        }
      >
        <Route index element={<AdminOverview />} />
        <Route path="stores" element={<AdminStores />} />
        <Route path="stores/:id" element={<AdminStoreDetail />} />
        <Route path="whatsapp-bot" element={<Navigate to="/admin" replace />} />
        <Route path="users" element={<AdminUsers />} />
        <Route path="manual-payments" element={<AdminManualPayments />} />
        <Route path="subscriptions" element={<AdminSubscriptions />} />
        <Route path="support-inbox" element={<AdminSupportInbox />} />
        <Route path="feature-access" element={<AdminFeatureAccess />} />
        <Route path="usage" element={<AdminUsageMonitor />} />
        <Route path="settings" element={<AdminSettings />} />
        <Route path="revenue" element={<AdminRevenue />} />
        <Route path="invoices" element={<AdminInvoices />} />
        <Route path="verification" element={<AdminVerification />} />
        <Route path="announcements" element={<AdminAnnouncements />} />
        <Route path="promos" element={<AdminPromoCodes />} />
        <Route path="analytics-plus" element={<AdminAnalyticsPlus />} />
        <Route path="audit-logs" element={<AdminAuditLogs />} />
        <Route path="*" element={<NotFound />} />
      </Route>
      {/* Public sales pages (landing pages) — kept from old system */}
      <Route element={<PreviewLayout />}>
        <Route path="/p/:slug" element={<PublicSalesPage />} />
      </Route>

      {/* ── Legacy storefront URL redirects ─────────────────────────────────
          Old /store/:storeSlug and /s/:storeId routes redirect permanently to
          the new block-based storefront at /ref/:storeSlug.
          This ensures no existing store links break for customers.
      ─────────────────────────────────────────────────────────────────────── */}
      <Route path="/store/:storeSlug" element={<StoreSlugRedirect />} />
      <Route path="/store/:storeSlug/product/:productSlug" element={<StoreSlugRedirect subPath="product" />} />
      <Route path="/store/:storeSlug/category/:categorySlug" element={<StoreSlugRedirect subPath="category" />} />
      <Route path="/store/:storeSlug/collections/:collectionSlug" element={<StoreSlugRedirect subPath="category" />} />
      <Route path="/store/:storeSlug/checkout" element={<StoreSlugRedirect subPath="checkout" />} />
      <Route path="/store/:storeSlug/:page" element={<StoreSlugRedirect />} />
      <Route path="/store/:storeSlug/*" element={<StoreSlugRedirect />} />

      {/* /s/:storeId routes (old short URLs) — redirect to /ref/ equivalent */}
      <Route path="/s/:storeId" element={<StoreIdRedirect />} />
      <Route path="/s/:storeId/:page?" element={<StoreIdRedirect />} />

      {/* Legacy demo-store / builder preview / editor preview → 404 */}
      <Route path="/demo-store/*" element={<NotFound />} />
      <Route path="/preview/*" element={<NotFound />} />
      <Route path="/order-success/:orderId" element={<OrderSuccess />} />
      <Route path="/order-failed/:orderId" element={<OrderFailed />} />
      <Route path="/track-order" element={<TrackOrder />} />
      <Route path="/maintenance" element={<Maintenance />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}
